import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const HowItWorksSection = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const steps = [
    {
      id: 1,
      icon: 'Link',
      title: language === 'bn' ? 'লিস্টিং URL পেস্ট করুন বা ছবি আপলোড করুন' : 'Paste Listing URL or Upload Photos/Videos',
      description: language === 'bn' ? 'Domain বা realestate.com.au থেকে সম্পত্তির URL কপি করুন, অথবা ছবি ও ভিডিও আপলোড করুন।' : 'Simply copy the property listing URL from Domain or realestate.com.au, or upload photos and videos of the property. Our AI works with both formats instantly.',
      color: 'primary',
      action: () => navigate('/dashboard'),
      actionLabel: language === 'bn' ? 'এখনই চেষ্টা করুন →' : 'Try It Now →'
    },
    {
      id: 2,
      icon: 'Brain',
      title: language === 'bn' ? 'এআই সম্পত্তির বিবরণ বিশ্লেষণ করে' : 'AI Analyzes Property Details',
      description: language === 'bn' ? 'আমাদের উন্নত এআই ইঞ্জিন সম্পত্তির তথ্য বের করে, বাজারের ডেটা তুলনা করে এবং ঝুঁকি চিহ্নিত করে।' : 'Our advanced AI engine extracts property information, compares market data, identifies risks, and evaluates pricing against comparable sales in the area.',
      color: 'accent',
      action: () => navigate('/analysis-detail'),
      actionLabel: language === 'bn' ? 'এআই বিশ্লেষণ দেখুন →' : 'See AI Analysis →'
    },
    {
      id: 3,
      icon: 'FileText',
      title: language === 'bn' ? 'মূল্য অন্তর্দৃষ্টি ও অফার সুপারিশ পান' : 'Get Pricing Insights & Offer Recommendations',
      description: language === 'bn' ? 'ন্যায্য মূল্য মূল্যায়ন, শক্তি, ঝুঁকি, প্রস্তাবিত অফার পরিসীমা সহ একটি বিস্তারিত প্রতিবেদন পান।' : 'Receive a comprehensive report with fair price assessment, strengths, risks, suggested offer range, and specific questions to ask the selling agent.',
      color: 'success',
      action: () => navigate('/offer-range-guidance'),
      actionLabel: language === 'bn' ? 'অফার গাইডেন্স দেখুন →' : 'View Offer Guidance →'
    }
  ];

  return (
    <section className="py-12 md:py-20 lg:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-muted rounded-full text-muted-foreground text-sm md:text-base font-medium mb-4">
            <Icon name="Lightbulb" size={16} color="currentColor" />
            <span>{language === 'bn' ? 'সহজ প্রক্রিয়া' : 'Simple Process'}</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-foreground mb-4 md:mb-6">
            {t('howItWorksTitle')}
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
            {language === 'bn' ? 'তিনটি সহজ ধাপে পেশাদার সম্পত্তি বিশ্লেষণ পান। কোনো বিশেষজ্ঞতার প্রয়োজন নেই।' : 'Get professional property analysis in three simple steps. No expertise required.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-12">
          {steps?.map((step, index) => (
            <div key={step?.id} className="relative">
              {index < steps?.length - 1 && (
                <div className="hidden md:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-border to-transparent -translate-x-1/2 z-0" />
              )}

              <div
                onClick={step?.action}
                className="relative bg-card rounded-2xl border border-border p-6 md:p-8 shadow-elevation-sm hover:shadow-elevation-md transition-all duration-300 h-full cursor-pointer group"
                style={{
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.5)';
                  e.currentTarget.style.boxShadow = '0 0 25px rgba(0, 212, 255, 0.2)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = '';
                  e.currentTarget.style.boxShadow = '';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div className="flex flex-col items-center text-center space-y-4 md:space-y-6">
                  <div className={`relative w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-2xl flex items-center justify-center bg-${step?.color}/10`}>
                    <Icon
                      name={step?.icon}
                      size={window.innerWidth >= 1024 ? 40 : window.innerWidth >= 768 ? 32 : 28}
                      color={`var(--color-${step?.color})`}
                    />
                    <div className={`absolute -top-3 -right-3 w-10 h-10 md:w-12 md:h-12 rounded-full bg-${step?.color} text-${step?.color}-foreground flex items-center justify-center font-bold text-lg md:text-xl shadow-elevation-md`}>
                      {step?.id}
                    </div>
                  </div>

                  <div className="space-y-2 md:space-y-3">
                    <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
                      {step?.title}
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                      {step?.description}
                    </p>
                  </div>

                  <div
                    className="mt-2 text-sm font-semibold"
                    style={{ color: `var(--color-${step?.color})` }}
                  >
                    {step?.actionLabel}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 lg:mt-20 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-muted/50 rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Clock" size={24} color="var(--color-primary)" />
              </div>
              <div className="text-left">
                <p className="text-lg md:text-xl font-semibold text-foreground">Average Analysis Time</p>
                <p className="text-2xl md:text-3xl font-bold text-primary text-data">30 seconds</p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-12 bg-border" />
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="Target" size={24} color="var(--color-success)" />
              </div>
              <div className="text-left">
                <p className="text-lg md:text-xl font-semibold text-foreground">AI Accuracy Rate</p>
                <p className="text-2xl md:text-3xl font-bold text-success text-data">94%</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-8 py-3 rounded-xl font-semibold text-base transition-all duration-200"
            style={{
              background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
              color: '#000000',
              boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 212, 255, 0.7)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 212, 255, 0.4)'}
          >
            Start Your First Analysis
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;